import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { NAV_LINKS, COMPANY } from "../constants/config";
import { useScrollSpy } from "../hooks/useScrollSpy";
import { animationVariants } from "../constants/animations";
import { scrollToTop } from "../utils/sectionNav";
import SectionLink from "./SectionLink";
import BrandLogo from "./BrandLogo";

const HEADER_HEIGHT = "h-[var(--nav-height)]";

function MenuIcon({ open }) {
  return (
    <span className="relative block h-5 w-6" aria-hidden="true">
      <span
        className={[
          "absolute left-0 top-0.5 block h-0.5 w-6 rounded-full bg-[#3d428a] transition-all duration-300",
          open ? "top-[9px] rotate-45" : "",
        ].join(" ")}
      />
      <span
        className={[
          "absolute left-0 top-[9px] block h-0.5 w-6 rounded-full bg-[#3d428a] transition-all duration-300",
          open ? "opacity-0" : "",
        ].join(" ")}
      />
      <span
        className={[
          "absolute bottom-0.5 left-0 block h-0.5 w-6 rounded-full bg-[#3d428a] transition-all duration-300",
          open ? "bottom-[9px] -rotate-45" : "",
        ].join(" ")}
      />
    </span>
  );
}

function Header() {
  const sectionIds = NAV_LINKS.map((link) => link.sectionId);
  const activeSection = useScrollSpy(sectionIds, 120);
  const [overHero, setOverHero] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);

  const textLinks = NAV_LINKS.filter((link) => !link.isCta);
  const contactLink = NAV_LINKS.find((link) => link.isCta);

  useEffect(() => {
    const onScroll = () => {
      const hero = document.getElementById("home");
      const heroBottom = hero
        ? hero.offsetTop + hero.offsetHeight
        : window.innerHeight * 0.75;
      setOverHero(window.scrollY < heroBottom - 80);
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  useEffect(() => {
    if (!mobileOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setMobileOpen(false);
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [mobileOpen]);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 1280) {
        setMobileOpen(false);
      }
    };

    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const closeMobileMenu = () => setMobileOpen(false);

  const getLinkClassName = (sectionId, isCta = false) => {
    const isActive = activeSection === sectionId;

    if (isCta) {
      return ["nav-cta", isActive ? "nav-cta-active" : ""]
        .filter(Boolean)
        .join(" ");
    }

    return ["nav-link", isActive ? "nav-link-active" : ""]
      .filter(Boolean)
      .join(" ");
  };

  const getMobileLinkClassName = (sectionId, isCta = false) => {
    const isActive = activeSection === sectionId;

    if (isCta) {
      return ["nav-mobile-cta", isActive ? "nav-mobile-cta-active" : ""]
        .filter(Boolean)
        .join(" ");
    }

    return ["nav-mobile-link", isActive ? "nav-mobile-link-active" : ""]
      .filter(Boolean)
      .join(" ");
  };

  const headerSurface = overHero ? "glass-nav-hero" : "glass-nav-scrolled";

  return (
    <>
      <motion.header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${headerSurface}`}
        variants={animationVariants.fadeIn}
        initial="hidden"
        animate="visible"
        role="navigation"
        aria-label="Main navigation"
      >
        <div
          className={`mx-auto flex w-full max-w-[1800px] items-center justify-between gap-3 px-3 sm:px-6 xl:grid xl:grid-cols-[auto_1fr_auto] xl:items-center xl:gap-6 lg:px-10 xl:px-14 2xl:px-20 ${HEADER_HEIGHT}`}
        >
          <motion.button
            type="button"
            onClick={scrollToTop}
            className="justify-self-start flex-shrink-0 overflow-visible bg-transparent p-0 focus:outline-none focus:ring-2 focus:ring-[#5d69be] focus:ring-offset-2 focus:ring-offset-white xl:mr-0"
            initial={{ opacity: 0, x: -24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ type: "spring", stiffness: 280, delay: 0.2 }}
            aria-label={`${COMPANY.name} - Click to go to top`}
          >
            <BrandLogo loading="eager" size="nav" taglineAlign="center" />
          </motion.button>

          <nav
            className="hidden items-center justify-center xl:flex xl:justify-self-center"
            aria-label="Primary desktop navigation"
          >
            <ul className="nav-pill flex items-center gap-1">
              {textLinks.map((link) => (
                <li key={link.sectionId}>
                  <SectionLink
                    sectionId={link.sectionId}
                    className={getLinkClassName(link.sectionId)}
                    aria-label={`Navigate to ${link.label}`}
                    aria-current={
                      activeSection === link.sectionId ? "page" : undefined
                    }
                  >
                    {link.label}
                    {activeSection === link.sectionId && (
                      <span className="absolute -bottom-0.5 left-1/2 hidden h-0.5 w-4 -translate-x-1/2 rounded-full bg-[#5d69be] xl:block" />
                    )}
                  </SectionLink>
                </li>
              ))}
            </ul>
          </nav>

          <div className="hidden items-center gap-3 xl:flex xl:justify-self-end">
            {contactLink && (
              <SectionLink
                sectionId={contactLink.sectionId}
                className={getLinkClassName(contactLink.sectionId, true)}
                aria-label={contactLink.label}
                aria-current={
                  activeSection === contactLink.sectionId ? "page" : undefined
                }
              >
                {contactLink.label}
              </SectionLink>
            )}
          </div>

          <button
            type="button"
            className="inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg border border-[#5d69be]/20 bg-[#eef0f8] text-[#3d428a] transition-colors hover:bg-white focus:outline-none focus:ring-2 focus:ring-[#5d69be] focus:ring-offset-2 xl:hidden"
            onClick={() => setMobileOpen((open) => !open)}
            aria-expanded={mobileOpen}
            aria-controls="mobile-nav-panel"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
          >
            <MenuIcon open={mobileOpen} />
          </button>
        </div>

        <AnimatePresence>
          {mobileOpen && (
            <>
              <motion.button
                type="button"
                className="fixed inset-x-0 bottom-0 top-[var(--nav-height)] z-40 bg-[#0a0a0f]/25 xl:hidden"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={closeMobileMenu}
                aria-label="Close menu overlay"
              />

              <motion.nav
                id="mobile-nav-panel"
                className="absolute inset-x-0 top-full z-50 w-full overflow-hidden border-b border-[#5d69be]/15 bg-white px-4 pb-5 pt-3 shadow-lg sm:px-6 xl:hidden"
                initial={{ opacity: 0, y: -12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                aria-label="Mobile navigation"
              >
                <ul className="flex flex-col gap-1">
                  {NAV_LINKS.map((link) => (
                    <li key={link.sectionId}>
                      <SectionLink
                        sectionId={link.sectionId}
                        className={getMobileLinkClassName(
                          link.sectionId,
                          link.isCta,
                        )}
                        onClick={closeMobileMenu}
                        aria-label={`Navigate to ${link.label}`}
                        aria-current={
                          activeSection === link.sectionId ? "page" : undefined
                        }
                      >
                        {link.label}
                      </SectionLink>
                    </li>
                  ))}
                </ul>
              </motion.nav>
            </>
          )}
        </AnimatePresence>
      </motion.header>

      <div className={HEADER_HEIGHT} aria-hidden="true" />
    </>
  );
}

export default Header;
